import { useRef, useState, type DragEvent, type ChangeEvent } from 'react'
import { getDroppedFiles } from '../shared/util/dropzone'

// ── FileUpload — drag-and-drop file upload with progress ──────────────────────

interface FileUploadProps {
  /** Accepted MIME types or extensions e.g. ".ps1,.bat,application/x-zip" */
  accept?: string
  /** Max file size in bytes. Default 50 MB. */
  maxBytes?: number
  /** Called when a valid file is selected */
  onFile: (file: File) => void
  /**
   * Extra check before `onFile` (after the built-in size check): returns the message to show instead of accepting the file,
   * or null to accept it. `accept` only filters the native picker — a dropped file bypasses it, so a caller that needs a
   * type (PDF only) checks it here.
   */
  validate?: (file: File) => string | null
  /** Upload progress 0-100, or undefined when not uploading */
  progress?: number
  /** Error message to display */
  error?: string | null
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function FileUpload({
  accept,
  maxBytes = 50 * 1024 * 1024,
  onFile,
  validate: extraCheck,
  progress,
  error,
}: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  function validate(file: File): boolean {
    if (file.size > maxBytes) {
      setValidationError(`Datei zu groß. Max. ${formatBytes(maxBytes)}.`)
      return false
    }
    const message = extraCheck?.(file) ?? null
    if (message) {
      setValidationError(message)
      return false
    }
    setValidationError(null)
    return true
  }

  function handleFile(file: File) {
    if (validate(file)) onFile(file)
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragging(false)
    const file = getDroppedFiles(e)[0]
    if (file) handleFile(file)
  }

  function onInputChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
    e.target.value = ''
  }

  const displayError = error ?? validationError

  return (
    <div className="w-full">
      <div
        onClick={() => inputRef.current?.click()}
        onDragEnter={(e) => { e.preventDefault(); setDragging(true) }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={[
          'border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center gap-3 cursor-pointer transition-colors',
          dragging
            ? 'border-primary bg-primary-container/30'
            : 'border-outline-variant hover:border-outline hover:bg-surface-container-low',
        ].join(' ')}
      >
        <span className="material-symbols-outlined text-outline-variant text-3xl">upload_file</span>
        <div className="text-center">
          <p className="text-sm font-medium text-on-surface">
            Datei hierher ziehen
          </p>
          <p className="text-xs text-on-surface-variant mt-0.5">
            oder klicken zum Auswählen · max. {formatBytes(maxBytes)}
          </p>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={onInputChange}
          className="hidden"
        />
      </div>

      {/* Progress bar */}
      {progress !== undefined && (
        <div className="mt-3">
          <div className="flex justify-between text-xs text-on-surface-variant mb-1">
            <span>Wird hochgeladen...</span>
            <span>{progress}%</span>
          </div>
          <div className="h-1.5 bg-surface-container rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Error */}
      {displayError && (
        <p className="mt-2 text-xs text-error flex items-center gap-1">
          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>error</span>
          {displayError}
        </p>
      )}
    </div>
  )
}
